import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'

import { AssetPicker } from '@/components/AssetPicker'
import { ContentQuiz } from '@/components/ContentQuiz'
import { Button } from '@/components/elements/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Separator } from '@/components/elements/separator'
import { Skeleton } from '@/components/elements/skeleton'
import { Textarea } from '@/components/elements/textarea'
import { Layout } from '@/components/Layout'
import { PDF_BLOCK_DEFAULTS, PdfBlockOptions } from '@/components/PdfBlockOptions'
import { useCoursesScope } from '@/pages/CoursesHub/useCoursesScope'
import {
  addEntityCourse,
  getEntityCourse,
  updateEntityCourse,
} from '@/services/entity_courses.service'
import { toast } from '@/utils/helpers'
import { getQuizConfigError } from '@/utils/quiz'

const STATE_OPTIONS = ['Draft', 'Published', 'Archived']
// PowerPoint is disabled everywhere for now (no working viewer); Brian converts
// decks to Articulate as needed. Re-add once its viewer works.
const CONTENT_TYPE_OPTIONS = ['Quiz Only', 'Video', 'PDF', 'Articulate']

const emptyContent = (order) => ({
  name: '',
  type: '',
  asset: null,
  assetLabel: '',
  quiz: null,
  order,
  ...PDF_BLOCK_DEFAULTS,
})

/**
 * Accounts / Course form — facility course create + edit.
 *
 * `courseId === 'new'` means create mode; otherwise edit. Content blocks use
 * the AssetPicker to bind a CourseAsset by id; Quiz Only blocks have no asset.
 */
export const CourseForm = () => {
  const { courseId } = useParams()
  const { entityId, organizationId, scopeId, scopeArgs, hubBase, rootCrumb, certLabel } =
    useCoursesScope()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isNew = courseId === 'new'

  const form = useForm({
    defaultValues: { name: '', description: '', state: 'Draft', contents: [] },
  })

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: 'contents',
  })

  const { data: course, isPending: loadingCourse } = useQuery({
    queryKey: ['accounts', 'courses', scopeId, courseId],
    queryFn: () => getEntityCourse({ ...scopeArgs, courseId }),
    placeholderData: keepPreviousData,
    enabled: !isNew,
  })

  useEffect(() => {
    if (course) {
      form.reset({
        name: course.name ?? '',
        description: course.description ?? '',
        state: course.state ?? 'Draft',
        contents: (course.contents ?? []).map((c) => ({
          id: c.id,
          name: c.name,
          type: c.type,
          asset: c.asset ?? null,
          // BE CourseContentSerializer exposes the attached asset's name as
          // asset_name → assetName after the axios camelCase interceptor.
          assetLabel: c.assetName ?? '',
          quiz: c.quiz ?? null,
          order: c.order ?? 0,
          pdfCompletionMode: c.pdfCompletionMode ?? PDF_BLOCK_DEFAULTS.pdfCompletionMode,
          pdfCompletionPercent: c.pdfCompletionPercent ?? null,
          pdfOrientation: c.pdfOrientation ?? PDF_BLOCK_DEFAULTS.pdfOrientation,
        })),
      })
    }
  }, [course, form])

  const { mutate: createCourse, isPending: pendingCreate } = useMutation({
    mutationFn: (data) => addEntityCourse({ ...scopeArgs, ...data }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast('Course created', 'success')
      queryClient.invalidateQueries({ queryKey: ['accounts', 'courses', scopeId] })
      navigate(`${hubBase}/course-modules/${data.id}`, {
        replace: true,
      })
    },
  })

  const { mutate: saveCourse, isPending: pendingSave } = useMutation({
    mutationFn: (data) => updateEntityCourse({ ...scopeArgs, id: courseId, ...data }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast(`${data.name} updated`, 'success')
      // Reset with the server response so dirty state clears and any new
      // content ids land in the form. Keep the existing assetLabel when the
      // bound asset id didn't change so the picker keeps showing the asset's
      // name — the BE's asset_name field covers the reload path, but the
      // writable CourseSerializer doesn't echo it back on update.
      const prevContents = form.getValues('contents') ?? []
      form.reset({
        name: data.name ?? '',
        description: data.description ?? '',
        state: data.state ?? 'Draft',
        contents: (data.contents ?? []).map((c, index) => {
          const prev = prevContents.find((p) => p.id && p.id === c.id) ?? prevContents[index]
          const assetId = c.asset ?? null
          const keptLabel = prev && prev.asset === assetId ? (prev.assetLabel ?? '') : ''
          return {
            id: c.id,
            name: c.name,
            type: c.type,
            asset: assetId,
            assetLabel: c.assetName ?? keptLabel,
            quiz: c.quiz ?? null,
            order: c.order ?? 0,
            pdfCompletionMode: c.pdfCompletionMode ?? PDF_BLOCK_DEFAULTS.pdfCompletionMode,
            pdfCompletionPercent: c.pdfCompletionPercent ?? null,
            pdfOrientation: c.pdfOrientation ?? PDF_BLOCK_DEFAULTS.pdfOrientation,
          }
        }),
      })
      queryClient.invalidateQueries({ queryKey: ['accounts', 'courses', scopeId] })
    },
  })

  const onSubmit = (values) => {
    // Drop abandoned blank rows (an "Add Block" left untouched), then require a
    // type on anything that remains — the backend rejects an empty content type
    // with a cryptic '"" is not a valid choice', so catch it here with a clear
    // message instead of a 400.
    const contents = (values.contents ?? []).filter((c) => c.type || c.name || c.asset)
    if (contents.some((c) => !c.type)) {
      toast('Each content block needs a type.', 'error')
      return undefined
    }

    // Strip the display-only assetLabel before submitting; reorder by index.
    const payload = {
      name: values.name,
      description: values.description,
      ...(isNew ? {} : { state: values.state }),
      contents: contents.map(({ assetLabel: _label, ...c }, index) => ({
        ...c,
        order: index,
      })),
    }
    // Catch an unpassable quiz before it reaches the API. The per-field rules
    // in ContentQuiz stop running once the block is collapsed, and the backend's
    // nested error doesn't survive getErrorMessage legibly (RSCX-385).
    const quizError = getQuizConfigError(payload.contents)
    if (quizError) {
      toast(quizError, 'error')
      return undefined
    }

    return isNew ? createCourse(payload) : saveCourse(payload)
  }

  // Breadcrumb back to the Custom Courses hub (Course Modules tab) — same
  // destination as the page's "Back to list" action.
  const customCoursesCrumb = {
    pathname: `${hubBase}/course-modules`,
    title: certLabel,
  }

  if (!isNew && loadingCourse) {
    return (
      <Layout breadcrumbs={[rootCrumb, customCoursesCrumb, 'Edit']}>
        <Layout.Heading title="Edit Course" />
        <Skeleton className="h-32 w-full" />
      </Layout>
    )
  }

  const heading = isNew ? 'New Course' : (course?.name ?? 'Edit Course')
  const { isDirty } = form.formState
  const submitting = pendingCreate || pendingSave

  return (
    <Layout
      breadcrumbs={[rootCrumb, customCoursesCrumb, isNew ? 'New' : (course?.name ?? 'Edit')]}
    >
      <div className="flex flex-row items-center justify-between">
        <Layout.Heading title={heading} />
        <div className="flex flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`${hubBase}/course-modules`)}
            disabled={submitting}
          >
            Back to list
          </Button>
          <Button onClick={form.handleSubmit(onSubmit)} disabled={!isDirty || submitting}>
            {isNew ? 'Create' : 'Save'}
          </Button>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 flex flex-col gap-4">
          <div className="flex max-w-2xl flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Annual Fire Safety Refresher" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={4} placeholder="What is this course about?" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isNew && (
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          <Separator className="my-4" />
          <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between">
              <div>
                <h3 className="text-base font-semibold">Content blocks</h3>
                <p className="text-muted-foreground text-xs">
                  Videos, PDFs, quizzes, and Articulate / SCORM content. Presentations are coming
                  soon!
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => append(emptyContent(fields.length))}
              >
                Add Block
              </Button>
            </div>

            {fields.length === 0 && (
              <p className="text-muted-foreground text-sm">
                No content yet. Add blocks for videos, PDFs, or quizzes.
              </p>
            )}

            {fields.map((field, index) => {
              const watchType = form.watch(`contents.${index}.type`)
              const watchAsset = form.watch(`contents.${index}.asset`)
              const watchAssetLabel = form.watch(`contents.${index}.assetLabel`)
              const watchQuiz = form.watch(`contents.${index}.quiz`)
              const needsAsset = watchType && watchType !== 'Quiz Only'

              return (
                <div key={field.id} className="flex flex-col gap-3 rounded border p-4 shadow-sm">
                  <div className="flex flex-row items-start gap-3">
                    <FormField
                      control={form.control}
                      name={`contents.${index}.name`}
                      rules={{ required: 'Block name is required' }}
                      render={({ field: f }) => (
                        <FormItem className="flex-1">
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Block name" {...f} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`contents.${index}.type`}
                      rules={{ required: 'Type is required' }}
                      render={({ field: f }) => (
                        <FormItem className="w-44">
                          <FormLabel>Type</FormLabel>
                          <Select
                            value={f.value}
                            onValueChange={(v) => {
                              // Switching type clears any incompatible asset.
                              f.onChange(v)
                              update(index, {
                                ...form.getValues(`contents.${index}`),
                                type: v,
                                asset: null,
                                assetLabel: '',
                              })
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                              {CONTENT_TYPE_OPTIONS.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-7"
                      onClick={() => remove(index)}
                      aria-label="Remove block"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {needsAsset && (
                    <div>
                      <FormLabel>Asset</FormLabel>
                      <AssetPicker
                        entityId={entityId}
                        organizationId={organizationId}
                        type={watchType}
                        value={watchAsset}
                        valueLabel={watchAssetLabel}
                        onChange={(asset) => {
                          update(index, {
                            ...form.getValues(`contents.${index}`),
                            asset: asset.id,
                            assetLabel: asset.name,
                          })
                        }}
                      />
                    </div>
                  )}

                  {watchType === 'PDF' && <PdfBlockOptions name={`contents.${index}`} />}

                  {watchQuiz && <ContentQuiz name={`contents.${index}`} />}

                  <Button
                    type="button"
                    variant={watchQuiz ? 'destructive' : 'outline'}
                    size="sm"
                    className="self-start"
                    onClick={() => {
                      update(index, {
                        ...form.getValues(`contents.${index}`),
                        quiz: watchQuiz
                          ? null
                          : {
                              questions: [],
                              name: '',
                              numQuestionsToPass: 0,
                              numQuestionsToDisplay: 0,
                            },
                      })
                    }}
                  >
                    {watchQuiz ? 'Remove Quiz' : 'Add Quiz'}
                  </Button>
                </div>
              )
            })}
          </div>
        </form>
      </Form>
    </Layout>
  )
}
